/**
 * "Sync now" dialog (desktop only).
 *
 * Opened from the corner status chip. Shows:
 *   - a per-table breakdown of how many local changes are waiting (entity → N),
 *   - a live percentage while uploading, and
 *   - a clear "everything is already synced" message when there is nothing to send.
 *
 * It drives the push via `window.electron.sync.now()` and tracks progress by
 * re-reading `sync.pending()` on each `sync:status` broadcast.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Stack,
} from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Friendly names for the internal entity keys stored in the sync queue.
const LABELS = {
  selfBill: 'Sales Bills',
  customerProfile: 'Customers',
  supplierProfile: 'Suppliers',
  clientProducts: 'Products',
};
const labelFor = (name) => LABELS[name] || name;

const OP_LABELS = { create: 'New', update: 'Edit', delete: 'Delete' };

export default function SyncDialog({ open, onClose }) {
  const api = window.electron && window.electron.sync;
  const [phase, setPhase] = useState('loading'); // loading|empty|ready|syncing|done|offline|error
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [startTotal, setStartTotal] = useState(0);
  // Writes the server rejected or that ran out of retries. They are kept locally and must
  // stay visible: without this the dialog reported "Everything is already synced".
  const [failedItems, setFailedItems] = useState([]);

  const loadFailed = useCallback(async () => {
    if (!api || !api.failed) return;
    setFailedItems((await api.failed()) || []);
  }, [api]);

  const load = useCallback(async () => {
    if (!api) return;
    try {
      const p = await api.pending();
      const t = (p && p.total) || 0;
      setRows((p && p.byEntity) || []);
      setTotal(t);
      await loadFailed();
      setPhase(t === 0 ? 'empty' : 'ready');
    } catch (_) {
      setPhase('error');
    }
  }, [api, loadFailed]);

  const retryFailed = async () => {
    try {
      await api.retryFailed();
      await load();
    } catch (_) {
      setPhase('error');
    }
  };

  // Keeps a copy of every failed change, with its original data, outside the app.
  const exportFailed = async () => {
    const items = (await api.failed()) || [];
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    await window.electron.saveFile({
      title: 'Export unsynced changes',
      defaultName: `younote-unsynced-changes-${stamp}.json`,
      filters: [{ name: 'JSON', extensions: ['json'] }],
      data: JSON.stringify({ exportedAt: new Date().toISOString(), items }, null, 2),
    });
  };

  const hasFailed = failedItems.length > 0;

  // (Re)load the breakdown whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setPhase('loading');
      load();
    }
  }, [open, load]);

  // While syncing, refresh the breakdown on every status broadcast.
  useEffect(() => {
    if (!open || !api) return undefined;
    const unsub = api.onStatus(async () => {
      const p = await api.pending();
      const t = (p && p.total) || 0;
      setRows((p && p.byEntity) || []);
      setTotal(t);
      await loadFailed();
      setPhase((cur) => {
        if (cur === 'syncing') return t === 0 ? 'done' : 'syncing';
        if (cur === 'ready' || cur === 'empty') return t === 0 ? 'empty' : 'ready';
        return cur;
      });
    });
    return unsub;
  }, [open, api, loadFailed]);

  const start = async () => {
    if (!api) return;
    setStartTotal(total);
    setPhase('syncing');
    try {
      const res = await api.now();
      if (!res || !res.online) {
        setPhase('offline');
      }
    } catch (_) {
      setPhase('error');
    }
  };

  const sent = Math.max(0, startTotal - total);
  const percent = startTotal > 0 ? Math.min(100, Math.round((sent / startTotal) * 100)) : 100;

  return (
    <Dialog open={open} onClose={phase === 'syncing' ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Sync to Server</DialogTitle>
      <DialogContent dividers>
        {phase === 'loading' && <Typography color="text.secondary">Checking for local changes…</Typography>}

        {phase === 'empty' && !hasFailed && (
          <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
            <CloudDoneIcon color="success" sx={{ fontSize: 48 }} />
            <Typography fontWeight={600}>Everything is already synced</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              All your data has been sent to the server. There is nothing waiting to upload.
            </Typography>
          </Stack>
        )}

        {phase === 'offline' && (
          <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
            <CloudOffIcon color="disabled" sx={{ fontSize: 48 }} />
            <Typography fontWeight={600}>You&apos;re offline</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Your {total} change{total > 1 ? 's are' : ' is'} saved safely and will sync automatically
              once you&apos;re back online.
            </Typography>
          </Stack>
        )}

        {phase === 'error' && (
          <Typography color="error">Could not read the sync queue. Please try again.</Typography>
        )}

        {(phase === 'ready' || phase === 'syncing' || phase === 'done') && (
          <>
            {phase === 'done' ? (
              <Stack alignItems="center" spacing={1} sx={{ pb: 1 }}>
                <CloudDoneIcon color="success" sx={{ fontSize: 44 }} />
                <Typography fontWeight={600}>All changes sent successfully</Typography>
                <Typography variant="body2" color="text.secondary">
                  {startTotal} record{startTotal > 1 ? 's' : ''} uploaded to the server.
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {phase === 'syncing'
                  ? `Uploading… ${sent} of ${startTotal}`
                  : `You have ${total} change${total > 1 ? 's' : ''} saved locally:`}
              </Typography>
            )}

            {phase === 'syncing' && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Progress</Typography>
                  <Typography variant="caption" color="text.secondary">{percent}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={percent} sx={{ mt: 0.5 }} />
              </Box>
            )}

            {rows.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">{phase === 'syncing' ? 'Remaining' : 'Pending'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.entity}>
                      <TableCell>{labelFor(r.entity)}</TableCell>
                      <TableCell align="right">{r.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}

        {hasFailed && phase !== 'loading' && phase !== 'error' && (
          <Box sx={{ mt: phase === 'empty' ? 0 : 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <ErrorOutlineIcon color="error" fontSize="small" />
              <Typography fontWeight={600} color="error">
                {failedItems.length} change{failedItems.length > 1 ? 's' : ''} could not be sent
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {failedItems.length > 1 ? 'They are' : 'It is'} kept safely on this computer. Retry, or export a copy.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failedItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{labelFor(item.entity)}</TableCell>
                    <TableCell>{OP_LABELS[item.op] || item.op}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{item.last_error || 'Unknown error'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {hasFailed && phase !== 'loading' && phase !== 'syncing' && (
          <>
            <Button onClick={exportFailed}>Export</Button>
            <Button color="warning" onClick={retryFailed}>Retry failed</Button>
          </>
        )}
        {phase === 'ready' && (
          <>
            <Button color="inherit" onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={start}>Send to Server</Button>
          </>
        )}
        {phase === 'syncing' && <Button disabled>Sending…</Button>}
        {(phase === 'empty' || phase === 'done' || phase === 'offline' || phase === 'error') && (
          <Button variant="contained" onClick={onClose}>Close</Button>
        )}
        {phase === 'loading' && <Button color="inherit" onClick={onClose}>Cancel</Button>}
      </DialogActions>
    </Dialog>
  );
}
